import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { COMPANY_VALUES } from '../data/mock';
import { Badge } from '../components/ui/badge';
import * as Icons from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.5, ease: 'easeOut' },
};

export default function AboutPage() {
  return (
    <main>
      {/* Hero */}
      <section className="pt-32 pb-16 px-6" style={{ background: 'radial-gradient(at 53% 78%, hsla(60,100%,50%,0.15) 0px, transparent 50%), radial-gradient(at 71% 91%, hsla(108,100%,50%,0.15) 0px, transparent 50%)' }}>
        <div className="max-w-[800px] mx-auto text-center">
          <motion.div {...fadeInUp}>
            <Badge className="mb-5 bg-[rgba(148,242,127,0.12)] text-[rgb(13,121,22)] border-[rgba(148,242,127,0.3)] px-4 py-1.5 text-xs font-medium rounded-full">
              Our Story
            </Badge>
          </motion.div>
          <motion.h1 {...fadeInUp} transition={{ delay: 0.1 }}
            className="font-bold text-[rgb(0,55,32)] tracking-tight mb-4"
            style={{ fontSize: 'clamp(2rem, 4.5vw, 3.5rem)', lineHeight: '0.92' }}>
            Built on 20+ Years of Tax Expertise
          </motion.h1>
          <motion.p {...fadeInUp} transition={{ delay: 0.2 }}
            className="text-[rgb(131,146,140)] max-w-[600px] mx-auto leading-relaxed" style={{ fontSize: 'clamp(1rem, 2vw, 1.15rem)' }}>
            We started Beginning to End Tax with a simple belief: everyone deserves access to knowledgeable, honest tax preparation—no matter where they live.
          </motion.p>
        </div>
      </section>

      {/* Story */}
      <section className="py-16 bg-white">
        <div className="max-w-[800px] mx-auto px-6">
          <motion.div {...fadeInUp} className="prose prose-sm max-w-none">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-8 items-start">
              <div className="bg-gradient-to-br from-[rgba(148,242,127,0.1)] to-[rgba(148,242,127,0.03)] rounded-2xl p-8 flex flex-col items-center text-center">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#8FEC78] to-[#81DD67] flex items-center justify-center mb-4">
                  <Icons.Award className="w-10 h-10 text-white" />
                </div>
                <span className="text-3xl font-bold text-[rgb(0,55,32)]">20+</span>
                <span className="text-sm text-[rgb(131,146,140)] mt-1">Years of Experience</span>
              </div>
              <div>
                <h2 className="font-semibold text-[rgb(0,55,32)] text-xl mb-4">From Beginning to End</h2>
                <p className="text-sm text-[rgb(14,15,12)] leading-relaxed mb-4">
                  Our team brings over two decades of experience in tax preparation, tax education, and business structure guidance. We've helped thousands of individuals and businesses navigate the complexities of the tax system.
                </p>
                <p className="text-sm text-[rgb(14,15,12)] leading-relaxed mb-4">
                  We believe that understanding your taxes is just as important as filing them. That's why we take an education-first approach—explaining not just what we're filing, but why, and what it means for your financial future.
                </p>
                <p className="text-sm text-[rgb(14,15,12)] leading-relaxed">
                  As a nationwide practice headquartered in Phoenix, AZ, we serve clients in all 50 states. Whether you're a W-2 employee, a freelancer, a small business owner, or someone looking to form a new entity, we have the expertise to guide you from beginning to end.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16" style={{ background: 'rgba(0,0,0,0.02)' }}>
        <div className="max-w-[1000px] mx-auto px-6">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <h2 className="font-semibold text-[rgb(0,55,32)] tracking-tight" style={{ fontSize: 'clamp(1.875rem, 4vw, 2.5rem)' }}>Our Values</h2>
            <p className="text-[rgb(131,146,140)] mt-2">The principles that guide everything we do.</p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {COMPANY_VALUES.map((value, i) => {
              const Icon = Icons[value.icon] || Icons.Star;
              return (
                <motion.div key={value.title} {...fadeInUp} transition={{ delay: i * 0.08 }}
                  className="bg-white border border-black/10 rounded-xl p-6 hover:shadow-md transition-shadow duration-200">
                  <div className="w-10 h-10 rounded-lg bg-[rgba(148,242,127,0.12)] flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-[rgb(13,121,22)]" />
                  </div>
                  <h3 className="font-semibold text-[rgb(0,55,32)] text-base mb-2">{value.title}</h3>
                  <p className="text-sm text-[rgb(131,146,140)] leading-relaxed">{value.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-[600px] mx-auto px-6 text-center">
          <motion.div {...fadeInUp}>
            <h2 className="font-semibold text-[rgb(0,55,32)] tracking-tight mb-3" style={{ fontSize: 'clamp(1.875rem, 4vw, 2.5rem)' }}>Let's Work Together</h2>
            <p className="text-[rgb(131,146,140)] mb-6">Ready to experience tax preparation done right? Start with a free consultation.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link to="/book" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gradient-to-br from-[#8FEC78] to-[#81DD67] text-white font-semibold text-base no-underline hover:shadow-lg transition-shadow">
                Book Free Consult <Icons.ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/start-here" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-black/10 text-[rgb(0,55,32)] font-medium text-base no-underline hover:bg-black/5 transition-colors">
                Start Here
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
